import React, { useState } from 'react';
import {
  Alert,
  Form,
  Input,
  Space,
  Popover,
  Button,
  FormInstance,
  Switch,
  Tooltip,
} from 'antd';
import { Param } from '../../cliDataSchema';
import useSearchGitRemoteRefQuery from '../../hooks/query/useSearchGitRemoteRefQuery';
import InlineMarkdown from '../InlineMarkdown';
import { QuestionCircleOutlined } from '@ant-design/icons';
import BranchTagSelect from '../inputs/BranchTagSelect';

type RemoteRefSelectProps = {
  form: FormInstance;
  handleSelectChange: (value: string) => void;
  param: Param;
  visibleParams: Param[];
};

const RemoteRefSelect: React.FC<RemoteRefSelectProps> = ({
  form,
  handleSelectChange,
  param,
}) => {
  const externalProjectUrl = Form.useWatch('external_project_url', form);
  const [freeTextSelected, setFreeTextSelected] = useState(false);

  const {
    data: gitRemoteRef,
    isLoading,
    error,
  } = useSearchGitRemoteRefQuery({
    repoUrl: externalProjectUrl || '',
    internalRepo: false,
  });

  const availableRefs = error
    ? []
    : [...(gitRemoteRef?.branches ?? []), ...(gitRemoteRef?.tags ?? [])];
  const noRefsAvailable = !isLoading && availableRefs.length === 0;
  const freeTextMode = freeTextSelected || noRefsAvailable;

  const handleChange = (value: string) => {
    form.setFieldsValue({ [param.name]: value });
    handleSelectChange(value);
  };

  const handleModeChange = (freeText: boolean) => {
    setFreeTextSelected(freeText);
    const value = form.getFieldValue(param.name);
    if (!freeText && value && !availableRefs.includes(value)) {
      handleChange('');
    }
  };

  return (
    <Form.Item
      key={param.name}
      required={param.required}
      label={
        <Space size={0}>
          {param.osep_generator_ui_display_name}
          {param.help && (
            <Popover
              content={<InlineMarkdown>{param.help}</InlineMarkdown>}
              title={param.osep_generator_ui_display_name}
              trigger="click"
            >
              <Button
                size="middle"
                icon={<QuestionCircleOutlined />}
                type="text"
              />
            </Popover>
          )}
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Tooltip
          title={
            noRefsAvailable
              ? 'No branches or tags could be loaded for this repository.'
              : undefined
          }
        >
          <Space>
            <Switch
              size="small"
              checked={freeTextMode}
              disabled={noRefsAvailable}
              onChange={handleModeChange}
            />
            <span>Free text</span>
          </Space>
        </Tooltip>
        <Form.Item
          noStyle
          name={param.name}
          normalize={freeTextMode ? (value) => value?.trim() : undefined}
          rules={[
            ...(param.required
              ? [{ required: true, message: 'This field is required.' }]
              : []),
            {
              validator: (_, value) =>
                !value || freeTextMode || availableRefs.includes(value)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        'Select a branch or tag from the list, or switch to free text.',
                      ),
                    ),
            },
          ]}
        >
          {freeTextMode ? (
            <Input placeholder="Enter a branch, tag or commit" />
          ) : (
            <BranchTagSelect
              onChange={handleChange}
              data={gitRemoteRef}
              isLoading={isLoading}
              error={error}
              placeholder="Select a reference"
              allowCustomValue={false}
            />
          )}
        </Form.Item>
        {freeTextMode && (
          <Alert
            type="warning"
            showIcon
            message="The generation of the project will fail if the specified ref does not exist in the remote repository."
          />
        )}
      </Space>
    </Form.Item>
  );
};

export default RemoteRefSelect;
